import { Vector3, Euler, Raycaster } from 'three';

export class MovementSystem {
    constructor({ camera, domElement, scene, spawnPoint = null }) {
        // Core components
        this.camera = camera;
        this.domElement = domElement;
        this.scene = scene;

        // Movement state
        this.moveForward = false;
        this.moveBackward = false;
        this.moveLeft = false;
        this.moveRight = false;
        this.isSprinting = false;

        // Camera look state
        this.euler = new Euler(0, 0, 0, 'YXZ');
        this.mouseSensitivity = 0.002;
        
        // Character position (separate from camera) - use spawn point if provided
        const defaultPosition = [0, 0, 10];
        const spawnPosition = spawnPoint?.position || defaultPosition;
        this.characterPosition = new Vector3(spawnPosition[0], spawnPosition[1], spawnPosition[2]);
        // Set initial character rotation from spawn point
        const spawnRotation = spawnPoint?.rotation || [0, 0, 0];
        this.characterRotation = { y: spawnRotation[1] }; // Use Y rotation from spawn point
        this.targetCharacterRotation = spawnRotation[1]; // Target rotation for smooth turning
        this.rotationSpeed = 8; // How fast character turns
        
        // Third-person camera settings
        this.cameraDistance = 8; // Distance behind character
        this.cameraHeight = 6;   // Height above character
        this.cameraYaw = 0;      // Camera horizontal orbit
        this.cameraPitch = -0.3; // Camera vertical orbit
        
        // Camera collision detection
        this.raycaster = new Raycaster();
        this.cameraCollisionOffset = 0.5; // Minimum distance from walls

        // Mode states
        this.isEnabled = false;
        this.isViewMode = false;
        this.isLocked = false;
        this.isFirstPerson = false;

        // Settings
        this.baseSpeed = 3.0;
        this.sprintMultiplier = 2.5;
        this.acceleration = 15.0;
        this.deceleration = 10.0;
        this.friction = 8.0;
        this.heightOffset = 0.5;
        
        
        // Velocity and momentum
        this.velocity = new Vector3(0, 0, 0);
        this.targetVelocity = new Vector3(0, 0, 0);
        this.currentSpeed = 0;
        this.targetSpeed = 0;
        
        // Movement state tracking for external systems
        this.isMoving = false;
        this.actualSpeed = 0;
        
        // Input buffering
        this.inputBuffer = {
            forward: { pressed: false, timePressed: 0 },
            backward: { pressed: false, timePressed: 0 },
            left: { pressed: false, timePressed: 0 },
            right: { pressed: false, timePressed: 0 },
            sprint: { pressed: false, timePressed: 0 }
        };
        this.bufferTime = 0.1; // 100ms buffer

        // Bind methods
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);

        // Touch state for mobile
        this.touchState = {
            isRotating: false,
            lastTouchX: 0,
            lastTouchY: 0,
            touchSensitivity: 0.003
        };

        // Set up pointer lock event listeners
        document.addEventListener('pointerlockchange', () => {
            this.isLocked = document.pointerLockElement === this.domElement;
        });
    }

    update(delta) {
        // Allow movement if in view mode OR if locked in edit mode
        if (!this.camera || (!this.isViewMode && !this.isLocked) || !this.isEnabled) {
            if (!this.camera) console.log('MovementSystem: No camera');
            // if (!this.isViewMode && !this.isLocked) console.log('MovementSystem: Not in view mode and not locked', { isViewMode: this.isViewMode, isLocked: this.isLocked });
            if (!this.isEnabled) console.log('MovementSystem: Not enabled');
            return;
        }

        // Get movement vectors based on camera mode
        let forward, right;
        
        if (this.isFirstPerson) {
            // First-person: movement relative to camera look direction
            forward = new Vector3(0, 0, -1);
            right = new Vector3(1, 0, 0);
            
            // Apply only Y rotation (yaw) to keep movement on ground plane
            const yaw = this.euler.y;
            const cosYaw = Math.cos(yaw);
            const sinYaw = Math.sin(yaw);
            
            forward.set(-sinYaw, 0, -cosYaw);
            right.set(cosYaw, 0, -sinYaw);
        } else {
            // Third-person: movement relative to orbital camera direction
            forward = new Vector3(
                -Math.sin(this.cameraYaw),
                0,
                -Math.cos(this.cameraYaw)
            );
            right = new Vector3(
                Math.cos(this.cameraYaw),
                0,
                -Math.sin(this.cameraYaw)
            );
        }

        // Process input buffer for smoother input handling
        const currentTime = performance.now() / 1000;
        
        // Update input states based on buffer
        const prevMoveForward = this.moveForward;
        this.moveForward = this.inputBuffer.forward.pressed || 
            (currentTime - this.inputBuffer.forward.timePressed < this.bufferTime);
        this.moveBackward = this.inputBuffer.backward.pressed || 
            (currentTime - this.inputBuffer.backward.timePressed < this.bufferTime);
        this.moveLeft = this.inputBuffer.left.pressed || 
            (currentTime - this.inputBuffer.left.timePressed < this.bufferTime);
        this.moveRight = this.inputBuffer.right.pressed || 
            (currentTime - this.inputBuffer.right.timePressed < this.bufferTime);
        this.isSprinting = this.inputBuffer.sprint.pressed || 
            (currentTime - this.inputBuffer.sprint.timePressed < this.bufferTime);
            
        // Debug log when movement states change
        if (prevMoveForward !== this.moveForward || this.moveLeft || this.moveRight || this.moveBackward) {
            console.log('Movement states in update:', {
                forward: this.moveForward,
                backward: this.moveBackward,
                left: this.moveLeft,
                right: this.moveRight,
                bufferPressed: {
                    forward: this.inputBuffer.forward.pressed,
                    left: this.inputBuffer.left.pressed,
                    right: this.inputBuffer.right.pressed,
                    backward: this.inputBuffer.backward.pressed
                }
            });
        }

        // Calculate desired direction
        const inputDirection = new Vector3(0, 0, 0);
        if (this.moveForward) inputDirection.add(forward);
        if (this.moveBackward) inputDirection.sub(forward);
        if (this.moveRight) inputDirection.add(right);
        if (this.moveLeft) inputDirection.sub(right);

        // Normalize input direction
        const hasInput = inputDirection.lengthSq() > 0;
        if (hasInput) {
            inputDirection.normalize();
        }

        // Calculate target speed
        this.targetSpeed = hasInput ? 
            (this.isSprinting ? this.baseSpeed * this.sprintMultiplier : this.baseSpeed) : 0;

        // Smooth speed transitions
        if (this.targetSpeed > this.currentSpeed) {
            // Accelerating
            this.currentSpeed = Math.min(this.targetSpeed, 
                this.currentSpeed + this.acceleration * delta);
        } else if (this.targetSpeed < this.currentSpeed) {
            // Decelerating
            this.currentSpeed = Math.max(this.targetSpeed, 
                this.currentSpeed - this.deceleration * delta);
        }

        // Calculate target velocity
        if (hasInput) {
            this.targetVelocity.copy(inputDirection).multiplyScalar(this.currentSpeed);
        } else {
            this.targetVelocity.set(0, 0, 0);
        }

        // Apply momentum/friction
        const lerpFactor = hasInput ? 
            Math.min(1, this.acceleration * delta) : 
            Math.min(1, this.friction * delta);
        
        this.velocity.lerp(this.targetVelocity, lerpFactor);

        // Apply movement with collision detection
        if (this.velocity.lengthSq() > 0.001) {
            const newPosition = this.characterPosition.clone();
            newPosition.addScaledVector(this.velocity, delta);
            
            // Check for wall collisions before moving - based purely on character position
            const collisionCheckedPosition = this.checkCharacterCollision(newPosition);
            this.characterPosition.copy(collisionCheckedPosition);
        }
        
        // Position camera 
        if (this.isFirstPerson) {
            // First-person: camera at character head level
            this.camera.position.set(
                this.characterPosition.x,
                this.characterPosition.y + 1.7, // Head level
                this.characterPosition.z
            );
        } else {
            // Third-person: orbital camera around character with collision detection
            const cameraOffsetX = Math.sin(this.cameraYaw) * Math.cos(this.cameraPitch) * this.cameraDistance;
            const cameraOffsetZ = Math.cos(this.cameraYaw) * Math.cos(this.cameraPitch) * this.cameraDistance;
            const cameraOffsetY = Math.sin(this.cameraPitch) * this.cameraDistance;
            
            // Calculate desired camera position
            const desiredCameraPosition = new Vector3(
                this.characterPosition.x + cameraOffsetX,
                this.characterPosition.y + this.cameraHeight + cameraOffsetY,
                this.characterPosition.z + cameraOffsetZ
            );
            
            // Check for collisions and adjust camera position
            const finalCameraPosition = this.checkCameraCollision(desiredCameraPosition);
            
            this.camera.position.copy(finalCameraPosition);
            
            // Make camera look at character
            const lookTarget = this.characterPosition.clone();
            lookTarget.y += 1.5; // Look at character's chest level
            this.camera.lookAt(lookTarget);
        }
        
        // Update character rotation to face movement direction with smooth turning
        if (hasInput) {
            this.targetCharacterRotation = Math.atan2(inputDirection.x, inputDirection.z) + Math.PI; // Added +Math.PI to flip 180 degrees
        }
        
        // Smooth character rotation
        const rotationDiff = this.targetCharacterRotation - this.characterRotation.y;
        const normalizedDiff = Math.atan2(Math.sin(rotationDiff), Math.cos(rotationDiff));
        this.characterRotation.y += normalizedDiff * this.rotationSpeed * delta;
        
        // Update movement state for external systems
        this.isMoving = this.velocity.lengthSq() > 0.001;
        this.actualSpeed = this.velocity.length();
    }

    handleMouseMove(event) {
        // Allow mouse look in both view mode and when locked
        if (!this.isEnabled || (!this.isViewMode && !this.isLocked)) return;

        const movementX = event.movementX || 0;
        const movementY = event.movementY || 0;

        if (this.isFirstPerson) {
            // First-person mouse look - directly rotate camera
            this.euler.y -= movementX * this.mouseSensitivity;
            this.euler.x -= movementY * this.mouseSensitivity;
            
            // Limit pitch to prevent camera flipping
            this.euler.x = Math.max(-Math.PI/2, Math.min(Math.PI/2, this.euler.x));
            
            // Apply rotation to camera
            this.camera.quaternion.setFromEuler(this.euler);
        } else {
            // Third-person mouse look - orbit camera around character
            this.cameraYaw -= movementX * this.mouseSensitivity;
            this.cameraPitch += movementY * this.mouseSensitivity; // Reversed pitch
            
            // Limit pitch to prevent camera flipping
            this.cameraPitch = Math.max(-Math.PI/2, Math.min(Math.PI/2, this.cameraPitch));
        }
    }

    handleTouchStart(event) {
        // Only handle single touch for camera rotation
        if (event.touches.length !== 1) return;
        
        const touch = event.touches[0];
        this.touchState.lastTouchX = touch.clientX;
        this.touchState.lastTouchY = touch.clientY;
        this.touchState.isRotating = true;
        
        // Prevent default to avoid scrolling
        event.preventDefault();
    }

    handleTouchMove(event) {
        if (!this.touchState.isRotating || event.touches.length !== 1) return;
        if (!this.isEnabled || (!this.isViewMode && !this.isLocked)) return;

        const touch = event.touches[0];
        const deltaX = touch.clientX - this.touchState.lastTouchX;
        const deltaY = touch.clientY - this.touchState.lastTouchY;

        if (this.isFirstPerson) {
            // First-person touch look - directly rotate camera
            this.euler.y -= deltaX * this.touchState.touchSensitivity;
            this.euler.x -= deltaY * this.touchState.touchSensitivity;
            
            // Limit pitch to prevent camera flipping
            this.euler.x = Math.max(-Math.PI/2, Math.min(Math.PI/2, this.euler.x));
            
            // Apply rotation to camera
            this.camera.quaternion.setFromEuler(this.euler);
        } else {
            // Third-person touch look - orbit camera around character
            this.cameraYaw -= deltaX * this.touchState.touchSensitivity;
            this.cameraPitch += deltaY * this.touchState.touchSensitivity;
            
            // Limit pitch to prevent camera flipping
            this.cameraPitch = Math.max(-Math.PI/2, Math.min(Math.PI/2, this.cameraPitch));
        }

        this.touchState.lastTouchX = touch.clientX;
        this.touchState.lastTouchY = touch.clientY;
        
        // Prevent default to avoid scrolling
        event.preventDefault();
    }

    handleTouchEnd(event) {
        this.touchState.isRotating = false;
        event.preventDefault();
    }

    lock() {
        if (!this.domElement) return;
        
        this.domElement.requestPointerLock = this.domElement.requestPointerLock ||
                                           this.domElement.mozRequestPointerLock ||
                                           this.domElement.webkitRequestPointerLock;

        if (this.domElement.requestPointerLock) {
            this.domElement.requestPointerLock();
        }
    }

    unlock() {
        document.exitPointerLock();
    }

    handleKeyDown(event) {
        // Allow movement in both view mode and when locked
        if (!this.isEnabled || (!this.isViewMode && !this.isLocked)) return;

        // Prevent key repeat
        if (event.repeat) return;

        const currentTime = performance.now() / 1000;

        switch (event.code) {
            case 'KeyW':
                this.moveForward = true;
                this.inputBuffer.forward.pressed = true;
                this.inputBuffer.forward.timePressed = currentTime;
                break;
            case 'KeyS':
                this.moveBackward = true;
                this.inputBuffer.backward.pressed = true;
                this.inputBuffer.backward.timePressed = currentTime;
                break;
            case 'KeyA':
                this.moveLeft = true;
                this.inputBuffer.left.pressed = true;
                this.inputBuffer.left.timePressed = currentTime;
                break;
            case 'KeyD':
                this.moveRight = true;
                this.inputBuffer.right.pressed = true;
                this.inputBuffer.right.timePressed = currentTime;
                break;
            case 'ShiftLeft':
            case 'ShiftRight':
                this.isSprinting = true;
                this.inputBuffer.sprint.pressed = true;
                this.inputBuffer.sprint.timePressed = currentTime;
                break;
            case 'KeyC':
                this.toggleFirstPerson();
                break;
        }
    }

    handleKeyUp(event) {
        const currentTime = performance.now() / 1000;

        switch (event.code) {
            case 'KeyW':
                this.moveForward = false;
                this.inputBuffer.forward.pressed = false;
                break;
            case 'KeyS':
                this.moveBackward = false;
                this.inputBuffer.backward.pressed = false;
                break;
            case 'KeyA':
                this.moveLeft = false;
                this.inputBuffer.left.pressed = false;
                break;
            case 'KeyD':
                this.moveRight = false;
                this.inputBuffer.right.pressed = false;
                break;
            case 'ShiftLeft':
            case 'ShiftRight':
                this.isSprinting = false;
                this.inputBuffer.sprint.pressed = false;
                break;
        }
    }

    setViewMode(isViewMode) {
        this.isViewMode = isViewMode;
        if (isViewMode) {
            this.enable();
        }
    }

    enable() {
        this.isEnabled = true;
        document.addEventListener('keydown', this.handleKeyDown);
        document.addEventListener('keyup', this.handleKeyUp);
        document.addEventListener('mousemove', this.handleMouseMove);
        
        // Add touch event listeners for mobile
        if (this.domElement) {
            this.domElement.addEventListener('touchstart', this.handleTouchStart, { passive: false });
            this.domElement.addEventListener('touchmove', this.handleTouchMove, { passive: false });
            this.domElement.addEventListener('touchend', this.handleTouchEnd, { passive: false });
        }
    }

    disable() {
        this.isEnabled = false;
        this.moveForward = false;
        this.moveBackward = false;
        this.moveLeft = false;
        this.moveRight = false;
        this.isSprinting = false;
        
        // Reset velocity and momentum
        this.velocity.set(0, 0, 0);
        this.targetVelocity.set(0, 0, 0);
        this.currentSpeed = 0;
        this.targetSpeed = 0;
        
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('keyup', this.handleKeyUp);
        document.removeEventListener('mousemove', this.handleMouseMove);
        
        // Remove touch event listeners
        if (this.domElement) {
            this.domElement.removeEventListener('touchstart', this.handleTouchStart);
            this.domElement.removeEventListener('touchmove', this.handleTouchMove);
            this.domElement.removeEventListener('touchend', this.handleTouchEnd);
        }
    }

    // Methods for AvatarTest compatibility
    getCharacterPosition() {
        return this.characterPosition;
    }
    
    getCharacterRotation() {
        return this.characterRotation;
    }
    
    getMovementState() {
        return {
            isMoving: this.isMoving,
            isSprinting: this.isSprinting,
            speed: this.actualSpeed,
            velocity: this.actualSpeed
        };
    }
    
    getViewMode() {
        return this.isFirstPerson ? 'first' : 'third';
    }

    // Method for external control (mobile buttons)
    setMovementState(direction, active) {
        switch(direction) {
            case 'forward':
                this.moveForward = active;
                break;
            case 'backward':
                this.moveBackward = active;
                break;
            case 'left':
                this.moveLeft = active;
                break;
            case 'right':
                this.moveRight = active;
                break;
            case 'sprint':
                this.isSprinting = active;
                break;
        }
    }

    // Method for joystick vector control
    setMovementVector(x, y) {
        // Convert joystick vector to movement states
        const threshold = 0.3;
        const currentTime = performance.now() / 1000; // Convert to seconds to match update loop
        
        // Update movement states
        this.moveLeft = x < -threshold;
        this.moveRight = x > threshold;
        this.moveForward = y > threshold;
        this.moveBackward = y < -threshold;
        
        console.log('setMovementVector called:', { 
            x, y, 
            left: this.moveLeft, 
            right: this.moveRight, 
            forward: this.moveForward, 
            backward: this.moveBackward 
        });
        
        // Also update input buffer to prevent override
        this.inputBuffer.left.pressed = this.moveLeft;
        this.inputBuffer.right.pressed = this.moveRight;
        this.inputBuffer.forward.pressed = this.moveForward;
        this.inputBuffer.backward.pressed = this.moveBackward;
        
        if (this.moveLeft) this.inputBuffer.left.timePressed = currentTime;
        if (this.moveRight) this.inputBuffer.right.timePressed = currentTime;
        if (this.moveForward) this.inputBuffer.forward.timePressed = currentTime;
        if (this.moveBackward) this.inputBuffer.backward.timePressed = currentTime;
    }

    checkCameraCollision(desiredPosition) {
        if (!this.scene || !this.camera) return desiredPosition;

        // Start ray from slightly above character to avoid floor collisions
        const rayStart = this.characterPosition.clone();
        rayStart.y += 1.5; // Start from character's chest level
        
        const direction = desiredPosition.clone().sub(rayStart).normalize();
        const distance = desiredPosition.distanceTo(rayStart);
        
        // Only check for collisions if camera is more than minimum distance away
        if (distance < 2) return desiredPosition;
        
        this.raycaster.set(rayStart, direction);
        this.raycaster.far = distance;
        this.raycaster.camera = this.camera; // Set camera for LineSegments2 raycasting
        
        const intersects = this.raycaster.intersectObjects(this.scene.children, true);
        
        // Filter out non-solid objects and be more selective
        const solidIntersects = intersects.filter(intersect => {
            const obj = intersect.object;
            // Only consider walls/room geometry, exclude floors and small objects
            return obj.material && 
                   obj.geometry &&
                   !obj.userData?.isDisplaySpot && 
                   !obj.userData?.isCharacter &&
                   !obj.userData?.isNonCollidable &&
                   !obj.userData?.isFloor &&
                   obj.visible &&
                   intersect.distance > 1; // Ignore very close intersections
        });
        
        if (solidIntersects.length > 0) {
            // Hit something - find a better position
            const hitPoint = solidIntersects[0].point;
            const pullBackDistance = Math.max(1, this.cameraCollisionOffset);
            
            // Pull camera back along ray direction, but don't go closer than minimum distance
            const pullBackPosition = rayStart.clone().add(
                direction.clone().multiplyScalar(
                    Math.max(2, solidIntersects[0].distance - pullBackDistance)
                )
            );
            
            // Ensure camera doesn't go below character level
            pullBackPosition.y = Math.max(
                pullBackPosition.y, 
                this.characterPosition.y + 1
            );
            
            return pullBackPosition;
        }
        
        return desiredPosition;
    }

    checkCharacterCollision(newPosition) {
        if (!this.scene) return newPosition;

        const currentPosition = this.characterPosition.clone();
        const capsuleRadius = 0.3; // Character capsule radius
        const capsuleHeight = 1.8; // Character capsule height
        
        // Simple distance check - is the new position too close to any walls?
        const movementVector = newPosition.clone().sub(currentPosition);
        const movementDistance = movementVector.length();
        
        // Only check collision if we're actually moving a reasonable distance
        if (movementDistance < 0.001) {
            return newPosition;
        }
        
        // Check if the character capsule would collide at the new position
        const hasCollision = this.checkCapsuleCollision(newPosition, capsuleRadius, capsuleHeight);
        
        if (hasCollision) {
            // Try to slide along walls by testing each axis separately
            const finalPosition = currentPosition.clone();
            
            // Try X movement only
            const testX = new Vector3(newPosition.x, currentPosition.y, currentPosition.z);
            const xCollision = this.checkCapsuleCollision(testX, capsuleRadius, capsuleHeight);
            if (!xCollision) {
                finalPosition.x = newPosition.x;
            }
            
            // Try Z movement only
            const testZ = new Vector3(finalPosition.x, currentPosition.y, newPosition.z);
            const zCollision = this.checkCapsuleCollision(testZ, capsuleRadius, capsuleHeight);
            if (!zCollision) {
                finalPosition.z = newPosition.z;
            }
            
            return finalPosition;
        }
        
        // No collision detected, allow full movement
        return newPosition;
    }

    checkCapsuleCollision(position, radius, height) {
        // Character capsule collision - check multiple points along the capsule height
        const testPoints = [
            new Vector3(position.x, position.y + 0.2, position.z),           // Feet level
            new Vector3(position.x, position.y + height * 0.5, position.z), // Waist level
            new Vector3(position.x, position.y + height * 0.8, position.z)  // Chest level
        ];
        
        // Check each point with horizontal rays
        for (const testPoint of testPoints) {
            if (this.checkPointCollision(testPoint, radius)) {
                return true;
            }
        }
        
        return false;
    }
    
    checkPointCollision(position, radius) {
        // Check horizontal directions for walls
        const directions = [
            new Vector3(1, 0, 0),   // Right
            new Vector3(-1, 0, 0),  // Left  
            new Vector3(0, 0, 1),   // Forward
            new Vector3(0, 0, -1)   // Backward
        ];
        
        for (const direction of directions) {
            this.raycaster.set(position, direction);
            this.raycaster.far = radius;
            this.raycaster.camera = this.camera;
            
            const intersects = this.raycaster.intersectObjects(this.scene.children, true);
            
            // Debug: log all intersections (disabled)
            // if (intersects.length > 0 && Math.random() < 0.1) {
            //     console.log('Ray intersections:', intersects.map(i => ({
            //         name: i.object.name,
            //         distance: i.distance,
            //         userData: i.object.userData,
            //         material: i.object.material?.type
            //     })));
            // }
            
            // Filter for walls - exclude display objects but allow room geometry
            const wallIntersects = intersects.filter(intersect => {
                const obj = intersect.object;
                const objName = obj.name ? obj.name.toLowerCase() : '';
                
                // Skip display objects and assets but allow room geometry
                if (obj.userData?.isDisplaySpot || 
                    obj.userData?.isCharacter ||
                    obj.userData?.isNonCollidable ||
                    obj.userData?.isAsset ||
                    obj.parent?.userData?.isDisplaySpot ||
                    obj.parent?.userData?.isAsset ||
                    obj.parent?.userData?.isNonCollidable ||
                    objName.includes('yae') ||
                    objName.includes('canvas') ||
                    objName.includes('frame') ||
                    objName.includes('image') ||
                    objName.includes('model') ||
                    objName.includes('display') ||
                    objName.includes('spot') ||
                    !obj.visible ||
                    !obj.material ||
                    !obj.geometry) {
                    return false;
                }
                
                // Skip floors and ceilings using userData
                if (obj.userData?.isFloor || obj.userData?.isCeiling) {
                    return false;
                }
                
                // Additional check: ignore horizontal surfaces (floors/ceilings) by normal
                if (intersect.face && intersect.face.normal) {
                    const normal = intersect.face.normal.clone();
                    normal.transformDirection(obj.matrixWorld);
                    // If surface normal is mostly vertical (pointing up/down), it's likely a floor/ceiling
                    if (Math.abs(normal.y) > 0.7) {
                        return false;
                    }
                }
                
                // Allow everything else that passed the filters (walls and room geometry)
                return intersect.distance < radius && intersect.distance > 0.01;
            });
            
            if (wallIntersects.length > 0) {
                // console.log('Wall collision detected:', wallIntersects.map(i => ({
                //     name: i.object.name,
                //     distance: i.distance,
                //     userData: i.object.userData
                // })));
                return true;
            }
        }
        
        return false;
    }

    toggleFirstPerson() {
        this.isFirstPerson = !this.isFirstPerson;
        
        if (this.isFirstPerson) {
            // Set up euler angles for first-person look
            this.euler.setFromQuaternion(this.camera.quaternion);
        }
        
        console.log(`Camera mode: ${this.isFirstPerson ? 'First-Person' : 'Third-Person'}`);
    }

    dispose() {
        this.disable();
    }
} 